import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Validation (same rules as the old Register.js) ───────────────────────────

const validate = (form) => {
  const errors = {};
  if (form.username.length < 3) {
    errors.username = 'Mínimo 3 caracteres';
  } else if (!/^[a-zA-Z0-9_]+$/.test(form.username)) {
    errors.username = 'Solo letras, números y guion bajo';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Email inválido';
  }
  if (form.password.length < 8) {
    errors.password = 'Mínimo 8 caracteres';
  } else if (!/[A-Z]/.test(form.password)) {
    errors.password = 'Debe incluir al menos una mayúscula';
  } else if (!/[0-9]/.test(form.password)) {
    errors.password = 'Debe incluir al menos un número';
  }
  if (form.confirm !== form.password) {
    errors.confirm = 'Las contraseñas no coinciden';
  }
  return errors;
};

// ── Register modal ────────────────────────────────────────────────────────────

function RegisterModal({ onClose }) {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    invitationCode: '', username: '', email: '', password: '', confirm: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [showPass, setShowPass]       = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);
    const errors = validate(form);
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    setLoading(true);
    try {
      await register(form.email, form.password, form.username, form.invitationCode);
      onClose();
      navigate('/home');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('invitación') || msg.includes('nválido') || msg.includes('expirado')) {
        setFieldErrors((prev) => ({ ...prev, invitationCode: msg }));
      } else if (msg.toLowerCase().includes('username')) {
        setFieldErrors((prev) => ({ ...prev, username: 'Ese nombre de usuario ya existe' }));
      } else if (msg.toLowerCase().includes('email')) {
        setFieldErrors((prev) => ({ ...prev, email: 'Ese email ya está registrado' }));
      } else {
        setServerError(msg || 'Error al crear la cuenta');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="modal-register">
        <button className="modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <h2>Crear cuenta</h2>
        {serverError && <p className="landing-error" style={{ marginBottom: '1rem' }}>{serverError}</p>}
        <form onSubmit={handleSubmit} noValidate>
          <div className="fgroup">
            <label>Código de invitación</label>
            <input
              className={`finput${fieldErrors.invitationCode ? ' finput--error' : ''}`}
              type="text"
              placeholder="xxxxxxxxxxxx"
              value={form.invitationCode}
              onChange={(e) => setField('invitationCode', e.target.value.trim())}
              autoComplete="off"
            />
            {fieldErrors.invitationCode && <p className="field-error">{fieldErrors.invitationCode}</p>}
          </div>
          <div className="fgroup">
            <label>Usuario</label>
            <input
              className={`finput${fieldErrors.username ? ' finput--error' : ''}`}
              type="text"
              placeholder="tunombre"
              value={form.username}
              onChange={(e) => setField('username', e.target.value)}
              autoComplete="username"
            />
            {fieldErrors.username && <p className="field-error">{fieldErrors.username}</p>}
          </div>
          <div className="fgroup">
            <label>Email</label>
            <input
              className={`finput${fieldErrors.email ? ' finput--error' : ''}`}
              type="email"
              placeholder="tu@email.com"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              autoComplete="email"
            />
            {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
          </div>
          <div className="fgroup">
            <label>Contraseña</label>
            <div className="password-wrapper">
              <input
                className={`finput${fieldErrors.password ? ' finput--error' : ''}`}
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setField('password', e.target.value)}
                autoComplete="new-password"
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPass((v) => !v)} tabIndex={-1}>
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
            {fieldErrors.password && <p className="field-error">{fieldErrors.password}</p>}
          </div>
          <div className="fgroup">
            <label>Confirmar contraseña</label>
            <input
              className={`finput${fieldErrors.confirm ? ' finput--error' : ''}`}
              type="password"
              placeholder="••••••••"
              value={form.confirm}
              onChange={(e) => setField('confirm', e.target.value)}
              autoComplete="new-password"
            />
            {fieldErrors.confirm && <p className="field-error">{fieldErrors.confirm}</p>}
          </div>
          <button type="submit" className="fsubmit" disabled={loading} style={{ marginTop: '0.5rem' }}>
            {loading ? 'Creando cuenta…' : 'Crear cuenta'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Landing page ──────────────────────────────────────────────────────────────

export default function LandingPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [form, setForm]               = useState({ email: '', password: '' });
  const [error, setError]             = useState(null);
  const [loading, setLoading]         = useState(false);
  const [showPass, setShowPass]       = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Usuario o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-container">
      {/* ── Left: visual ── */}
      <div className="landing-visual">
        <video autoPlay loop muted playsInline>
          <source src="/chibi-fox.mp4" type="video/mp4" />
        </video>
      </div>

      {/* ── Right: content ── */}
      <div className="landing-content">

        {/* Identity */}
        <div className="landing-identity">
          <h1 className="landing-title">
            {'ASHVEIL'.split('').map((letter, i) => (
              <span
                key={i}
                className="landing-title-letter"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                {letter}
              </span>
            ))}
          </h1>
          <p className="landing-quote">Hay lugares que no aparecen en los mapas.</p>
          <p className="landing-subquote">
            Ashveil es uno de ellos.<br />
            Si llegaste hasta aquí, alguien confió en vos.
          </p>
        </div>

        {/* Divider */}
        <div className="landing-divider" />

        {/* Login form */}
        <div className="landing-form-wrapper">
          <form className="landing-form" onSubmit={handleLogin} noValidate>
            <input
              type="text"
              placeholder="Email o usuario"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              autoComplete="username"
            />
            <div className="password-wrapper">
              <input
                type={showPass ? 'text' : 'password'}
                placeholder="Contraseña"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="pw-toggle"
                onClick={() => setShowPass((v) => !v)}
                tabIndex={-1}
              >
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
            {error && <p className="landing-error">{error}</p>}
            <button type="submit" className="btn-enter" disabled={loading}>
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
          <p className="landing-invite-link">
            ¿Tenés un código de invitación?{' '}
            <span onClick={() => setShowRegister(true)}>Registrate</span>
          </p>
        </div>

        {/* Disclaimer */}
        <div className="landing-disclaimer">
          <p>Ashveil no aloja contenido multimedia.</p>
          <p>Funciona como agregador de enlaces hacia fuentes externas.</p>
          <p>Si sos titular de contenido, escribinos: contacto@ashveil.uk</p>
          <p>Uso estrictamente personal y sin fines de lucro.</p>
        </div>
      </div>

      {showRegister && (
        <RegisterModal onClose={() => setShowRegister(false)} />
      )}
    </div>
  );
}
